import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, CreditCard, Tag, Check, X, Sparkles, ShieldCheck, Lock,
  Package as PackageIcon, Rocket, Crown, TrendingUp, Users, Star, Zap, Loader2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import axios from 'axios';
import { toast } from '../hooks/use-toast';
import { useCashfreeSdk } from '../lib/useCashfreeSdk';
import { fbInitiateCheckout, fbAddPaymentInfo, fbViewContent } from '../lib/fbpixel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_PRICES = { Basic: 999, Advanced: 1999, Professional: 2999 };
const ORIGINAL_PRICES = { Basic: 1999, Advanced: 3999, Professional: 5999 };
const PLAN_META = {
  Basic: { icon: PackageIcon, tagline: 'Perfect for Beginners', accent: 'from-blue-500 to-blue-600' },
  Advanced: { icon: Rocket, tagline: 'Most Popular Choice', accent: 'from-fuchsia-500 to-rose-500' },
  Professional: { icon: Crown, tagline: 'For Serious Entrepreneurs', accent: 'from-amber-500 to-orange-600' },
};
const PLAN_FEATURES = {
  Basic: ['Trending Products', 'Complete Website Setup', 'Single Ad Campaign', '24x7 WhatsApp Support'],
  Advanced: ['Medium Margin Products', 'Premium Theme', '3 Ad Campaigns', 'Personal Account Manager'],
  Professional: ['High Margin Products', 'Premium Branding', '5 Ad Campaigns', 'VIP Priority Support'],
};

// Animated counter that smoothly transitions to a new value
const AnimatedPrice = ({ value, className }) => {
  const [display, setDisplay] = useState(value);
  useEffect(() => {
    const start = display;
    const end = value;
    if (start === end) return;
    const duration = 500;
    const startTime = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - startTime) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(Math.round(start + (end - start) * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => raf && cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return <span className={className}>₹{display.toLocaleString('en-IN')}</span>;
};

const Payment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { loaded: cfLoaded, Cashfree } = useCashfreeSdk();

  // Accept plan as either a string ('Basic') or an object ({name, price, ...}) from any caller
  const rawPlan = location.state?.plan;
  const planName = typeof rawPlan === 'string' ? rawPlan : rawPlan?.name;

  const [paymentSettings, setPaymentSettings] = useState(null);
  const [planLoading, setPlanLoading] = useState(true);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '' });
  const [loading, setLoading] = useState(false);
  const [couponInput, setCouponInput] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState('');

  // Redirect to /enroll if no plan was passed in
  useEffect(() => {
    if (!planName) {
      toast({ title: 'Choose a plan first', description: 'Redirecting you to enrol…' });
      navigate('/enroll', { replace: true });
    }
  }, [planName, navigate]);

  // Always fetch the latest server-side price (admin-editable)
  useEffect(() => {
    if (!planName) return;
    (async () => {
      setPlanLoading(true);
      try {
        const res = await axios.get(`${API}/payment-settings`);
        setPaymentSettings(res.data || null);
      } catch (_e) {
        setPaymentSettings(null);
      } finally {
        setPlanLoading(false);
      }
    })();
  }, [planName]);

  // Auto-fill user info if logged in
  useEffect(() => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        setFormData((prev) => ({
          name: prev.name || user?.name || '',
          email: prev.email || user?.email || '',
          phone: prev.phone || user?.phone || '',
        }));
      }
    } catch (_e) { /* ignore */ }
  }, []);

  // Derive a normalized plan object that always has a price
  const plan = useMemo(() => {
    if (!planName) return null;
    const fromSettings = paymentSettings?.plans?.[planName]?.price;
    const fromState = typeof rawPlan === 'object' ? rawPlan?.price : undefined;
    const price = Number(fromSettings ?? fromState ?? DEFAULT_PRICES[planName] ?? 0);
    const originalPrice = Number(
      (typeof rawPlan === 'object' ? rawPlan?.originalPrice : null) ?? ORIGINAL_PRICES[planName] ?? 0
    );
    const meta = PLAN_META[planName] || PLAN_META.Basic;
    return {
      name: planName,
      price,
      originalPrice,
      tagline: meta.tagline,
      accent: meta.accent,
      Icon: meta.icon,
      features: PLAN_FEATURES[planName] || [],
      isUpgrade: typeof rawPlan === 'object' ? !!rawPlan?.isUpgrade : false,
    };
  }, [planName, paymentSettings, rawPlan]);

  // Facebook Pixel: ViewContent (after we know the actual price)
  useEffect(() => {
    if (plan?.name && plan.price) {
      try {
        fbViewContent({
          content_name: plan.name,
          content_ids: [plan.name],
          value: Number(plan.price) || 0,
          content_type: 'product',
        });
      } catch (_e) { /* silent */ }
    }
  }, [plan?.name, plan?.price]);

  const effectivePrice = appliedCoupon ? appliedCoupon.finalPrice : plan?.price || 0;
  const discount = plan && plan.originalPrice > plan.price ? plan.originalPrice - plan.price : 0;
  const totalSavings = (discount || 0) + (appliedCoupon?.discount || 0);

  const handleApplyCoupon = async () => {
    if (!plan) return;
    const code = (couponInput || '').trim().toUpperCase();
    if (!code) {
      setCouponError('Enter a coupon code');
      return;
    }
    setCouponLoading(true);
    setCouponError('');
    try {
      const res = await axios.post(`${API}/coupons/validate`, {
        code,
        planName: plan.name,
        originalPrice: plan.price,
      });
      if (res.data && res.data.valid) {
        setAppliedCoupon({
          code: res.data.code,
          finalPrice: res.data.finalPrice,
          discount: res.data.discount,
          message: res.data.message,
        });
        toast({ title: '🎉 Coupon Applied!', description: res.data.message });
      } else {
        setAppliedCoupon(null);
        setCouponError(res.data?.message || 'Invalid coupon');
      }
    } catch (_e) {
      setCouponError('Could not validate coupon. Try again.');
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponInput('');
    setCouponError('');
  };

  const handleCashfreePay = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone) {
      toast({
        title: 'Missing Information',
        description: 'Please fill in name, email and phone.',
        variant: 'destructive',
      });
      return;
    }
    if (!cfLoaded || !Cashfree) {
      toast({
        title: 'Loading…',
        description: 'Payment SDK is still loading. Please try again in a moment.',
      });
      return;
    }
    setLoading(true);
    try {
      const userStr = localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;
      const returnUrl = `${window.location.origin}/payment-return?order_id={order_id}`;

      try {
        fbInitiateCheckout({
          value: Number(effectivePrice) || 0,
          content_name: plan.name,
          content_ids: [plan.name],
          num_items: 1,
        });
        fbAddPaymentInfo({ value: Number(effectivePrice) || 0, content_name: plan.name });
      } catch (_e) { /* silent */ }

      const { data } = await axios.post(`${API}/cashfree/create-order`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        planName: plan.name,
        couponCode: appliedCoupon?.code || null,
        userId: user?.principalId || user?.id || '',
        returnUrl,
      });
      if (!data?.payment_session_id) throw new Error('Could not initialise payment session');
      const cashfree = new Cashfree({ mode: (data.cashfree_env || 'PROD') === 'PROD' ? 'production' : 'sandbox' });
      await cashfree.checkout({ paymentSessionId: data.payment_session_id, redirectTarget: '_self' });
    } catch (err) {
      console.error('Cashfree payment error:', err);
      toast({
        title: 'Payment Failed',
        description: err.response?.data?.detail || err.message || 'Could not start payment',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  if (!plan) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-blue-500 animate-spin mx-auto mb-3" />
          <p className="text-slate-500 text-sm">Loading your plan…</p>
        </div>
      </div>
    );
  }

  const PlanIcon = plan.Icon || PackageIcon;

  return (
    <div className="min-h-screen text-slate-800 relative overflow-x-hidden pb-32 sm:pb-12">
      {/* Animated background */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-fuchsia-50" />
        <div className="absolute -top-40 -left-40 w-96 h-96 bg-blue-300/30 rounded-full blur-[120px] animate-pulse" />
        <div className="absolute top-1/3 -right-40 w-96 h-96 bg-fuchsia-300/30 rounded-full blur-[120px] animate-pulse [animation-delay:1s]" />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-96 bg-amber-200/20 rounded-full blur-[140px] animate-pulse [animation-delay:2s]" />
      </div>

      <div className="relative z-10">
        {/* Sticky compact header */}
        <header className="sticky top-0 z-30 backdrop-blur-xl bg-white/85 border-b border-slate-200/80 safe-area-inset-top">
          <div className="container mx-auto max-w-5xl px-3 sm:px-6 py-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(-1)}
                data-testid="payment-back-btn"
                className="hover:bg-slate-100 text-slate-600 px-2 -ml-2 flex-shrink-0"
              >
                <ArrowLeft className="w-4 h-4 sm:mr-1" />
                <span className="hidden sm:inline">Back</span>
              </Button>
              <button
                type="button"
                onClick={() => navigate('/')}
                className="flex items-center gap-2 group flex-shrink-0"
                data-testid="payment-home-logo"
              >
                <img
                  src="/dropsquadz-logo.jpeg"
                  alt="DropSquadz"
                  className="w-8 h-8 sm:w-9 sm:h-9 rounded-full ring-2 ring-blue-500/40 group-hover:ring-blue-500 transition-all object-cover"
                />
                <span className="font-bold text-blue-600 hidden sm:inline">DropSquadz</span>
              </button>
            </div>

            <div className="flex items-center gap-2 text-xs text-slate-600 flex-shrink-0">
              <ShieldCheck className="w-4 h-4 text-green-500" />
              <span className="hidden sm:inline">256-bit Secure · </span>
              <span className="font-semibold">Powered by Cashfree</span>
            </div>
          </div>
        </header>

        <main className="container mx-auto max-w-5xl px-3 sm:px-6 py-4 sm:py-8">
          {/* Hero summary card */}
          <div className="mb-4 sm:mb-6 rounded-2xl sm:rounded-3xl overflow-hidden bg-white shadow-xl shadow-blue-500/5 border border-slate-200">
            <div className={`bg-gradient-to-br ${plan.accent} px-4 sm:px-6 py-5 sm:py-6 text-white relative overflow-hidden`}>
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.25),transparent_40%)]" />
              <div className="relative flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-white/20 backdrop-blur-sm text-[10px] sm:text-xs font-bold uppercase tracking-wider mb-2">
                    <Sparkles className="w-3 h-3" /> Order Summary
                  </div>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-9 h-9 sm:w-11 sm:h-11 bg-white/25 rounded-xl flex items-center justify-center shadow-inner">
                      <PlanIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                    </div>
                    <div>
                      <h1 className="text-xl sm:text-2xl font-extrabold leading-tight">{plan.name} Plan</h1>
                      <p className="text-[11px] sm:text-xs text-white/80">{plan.tagline}</p>
                    </div>
                  </div>
                </div>

                <div className="text-right flex-shrink-0">
                  {planLoading ? (
                    <Loader2 className="w-6 h-6 animate-spin text-white/80" />
                  ) : (
                    <>
                      <AnimatedPrice value={effectivePrice} className="text-3xl sm:text-4xl font-black tracking-tight" />
                      {(plan.originalPrice > effectivePrice || appliedCoupon) && (
                        <p className="text-xs sm:text-sm text-white/70 line-through">₹{(appliedCoupon ? plan.price : plan.originalPrice).toLocaleString('en-IN')}</p>
                      )}
                    </>
                  )}
                </div>
              </div>

              {totalSavings > 0 && (
                <div className="relative mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-400/95 text-emerald-900 text-[11px] sm:text-xs font-bold shadow-lg">
                  <TrendingUp className="w-3 h-3" />
                  You save ₹{totalSavings.toLocaleString('en-IN')}
                </div>
              )}
            </div>

            {/* Features strip */}
            <div className="px-4 sm:px-6 py-4 bg-slate-50/50 border-b border-slate-200/80">
              <div className="grid grid-cols-2 gap-x-3 gap-y-2">
                {plan.features.map((f, i) => (
                  <div key={i} className="flex items-start gap-1.5 text-[11px] sm:text-xs text-slate-700">
                    <Check className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Coupon section */}
            <div className="px-4 sm:px-6 py-4">
              {!appliedCoupon ? (
                <div className="space-y-2">
                  <Label className="text-[11px] uppercase tracking-wide text-slate-500 flex items-center gap-1.5 font-semibold">
                    <Tag className="w-3.5 h-3.5 text-orange-500" /> Have a coupon?
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Enter coupon code"
                      value={couponInput}
                      onChange={(e) => { setCouponInput(e.target.value.toUpperCase()); setCouponError(''); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleApplyCoupon(); } }}
                      className="bg-white border-slate-300 text-slate-800 uppercase tracking-wider font-semibold h-11"
                      data-testid="payment-coupon-input"
                    />
                    <Button
                      type="button"
                      onClick={handleApplyCoupon}
                      disabled={couponLoading}
                      data-testid="payment-coupon-apply"
                      className="bg-orange-500 hover:bg-orange-600 text-white font-bold whitespace-nowrap px-4 h-11"
                    >
                      {couponLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
                    </Button>
                  </div>
                  {couponError && (
                    <p className="text-xs text-red-600 flex items-center gap-1">
                      <X className="w-3 h-3" /> {couponError}
                    </p>
                  )}
                  {plan.name === 'Basic' && (
                    <button
                      type="button"
                      onClick={() => { setCouponInput('JULY2026'); setTimeout(handleApplyCoupon, 50); }}
                      className="w-full mt-1 p-2.5 border-2 border-dashed border-orange-300 bg-orange-50 rounded-lg text-xs text-orange-700 font-semibold hover:bg-orange-100 transition-all flex items-center justify-center gap-1.5 active:scale-[0.98]"
                      data-testid="payment-coupon-suggest"
                    >
                      <Sparkles className="w-3.5 h-3.5" />
                      Tap to apply <span className="font-mono font-bold">JULY2026</span> — Save ₹{Math.max(0, plan.price - 9)}
                    </button>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-between p-3 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl shadow-sm">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="w-9 h-9 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-md shadow-green-500/30">
                      <Check className="w-5 h-5 text-white" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-green-800 font-mono">{appliedCoupon.code}</p>
                      <p className="text-[10px] text-green-700 truncate">Saved ₹{Math.round(appliedCoupon.discount).toLocaleString('en-IN')}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleRemoveCoupon}
                    aria-label="Remove coupon"
                    className="p-2 text-slate-500 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors flex-shrink-0"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Trust badges */}
          <div className="mb-4 sm:mb-6 grid grid-cols-3 gap-2 sm:gap-3">
            {[
              { Icon: Users, t: '5,415+', s: 'Clients', accent: 'text-blue-600 bg-blue-100' },
              { Icon: Star, t: '4.8/5', s: 'Rating', accent: 'text-amber-600 bg-amber-100' },
              { Icon: ShieldCheck, t: '7-Day', s: 'Refund', accent: 'text-green-600 bg-green-100' },
            ].map(({ Icon, t, s, accent }, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-xl p-2.5 sm:p-3 text-center shadow-sm">
                <div className={`w-8 h-8 mx-auto rounded-full flex items-center justify-center mb-1 ${accent}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <p className="text-xs sm:text-sm font-bold text-slate-800">{t}</p>
                <p className="text-[10px] text-slate-500">{s}</p>
              </div>
            ))}
          </div>

          {/* Payment form card */}
          <form
            onSubmit={handleCashfreePay}
            className="bg-white rounded-2xl sm:rounded-3xl shadow-xl shadow-blue-500/5 border border-slate-200 overflow-hidden"
            data-testid="payment-form"
          >
            <div className="px-4 sm:px-6 py-4 border-b border-slate-200 flex items-center gap-2">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center shadow-md shadow-blue-500/30">
                <CreditCard className="w-4 h-4 text-white" />
              </div>
              <div>
                <h2 className="text-base sm:text-lg font-bold text-slate-800">Your details</h2>
                <p className="text-[11px] text-slate-500">We&apos;ll send order updates here.</p>
              </div>
            </div>

            <div className="px-4 sm:px-6 py-4 space-y-3">
              <div>
                <Label htmlFor="name" className="text-[11px] uppercase tracking-wide text-slate-500 mb-1 inline-block">Full name *</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Your full name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="payment-name-input"
                  className="h-12 bg-slate-50 border-slate-200"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-[11px] uppercase tracking-wide text-slate-500 mb-1 inline-block">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="payment-email-input"
                  className="h-12 bg-slate-50 border-slate-200"
                />
              </div>

              <div>
                <Label htmlFor="phone" className="text-[11px] uppercase tracking-wide text-slate-500 mb-1 inline-block">Phone *</Label>
                <Input
                  id="phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="10-digit mobile number"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                  required
                  data-testid="payment-phone-input"
                  className="h-12 bg-slate-50 border-slate-200"
                />
              </div>
            </div>

            {/* Accepted methods row */}
            <div className="px-4 sm:px-6 pb-3">
              <div className="flex items-center justify-center gap-2 sm:gap-3 text-[10px] sm:text-xs text-slate-500">
                <span>Accepted:</span>
                {['UPI', 'Cards', 'Net Banking', 'Wallets'].map((m) => (
                  <span key={m} className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 font-semibold">{m}</span>
                ))}
              </div>
            </div>

            {/* Skip-for-now & footer text — main CTA is in sticky bar below on mobile */}
            <div className="px-4 sm:px-6 pb-4 sm:pb-6 hidden sm:block">
              <Button
                type="submit"
                disabled={loading || !cfLoaded || planLoading}
                data-testid="payment-pay-btn-desktop"
                className="w-full bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 hover:from-blue-700 hover:via-indigo-700 hover:to-indigo-800 text-white shadow-lg shadow-blue-500/30 transition-all hover:scale-[1.01] active:scale-[0.99] py-6 text-base font-bold rounded-xl"
              >
                {loading ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Starting payment…</>
                ) : !cfLoaded ? (
                  <>Loading secure checkout…</>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <Lock className="w-4 h-4" />
                    Pay <AnimatedPrice value={effectivePrice} className="" /> Securely
                    <Zap className="w-4 h-4" />
                  </span>
                )}
              </Button>

              <Button
                type="button"
                onClick={() => navigate('/dashboard')}
                variant="outline"
                className="w-full mt-2 border-2 border-slate-200 text-slate-600 hover:bg-slate-50 py-5 text-sm font-medium"
                data-testid="payment-skip-btn"
              >
                Skip for now, I&apos;ll pay later
              </Button>

              <p className="text-[11px] text-slate-400 text-center mt-3 flex items-center justify-center gap-1">
                <Lock className="w-3 h-3" /> By proceeding you agree to DropSquadz Terms &amp; Refund Policy.
              </p>
            </div>
          </form>

          {/* Skip-for-now on mobile (above sticky CTA bar) */}
          <div className="mt-3 sm:hidden">
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="w-full text-center text-sm text-slate-500 underline-offset-2 hover:underline py-2"
              data-testid="payment-skip-btn-mobile"
            >
              Skip for now, I&apos;ll pay later
            </button>
          </div>
        </main>

        {/* Sticky mobile CTA */}
        <div
          className="fixed bottom-0 left-0 right-0 z-40 sm:hidden bg-white/95 backdrop-blur-xl border-t border-slate-200 shadow-[0_-8px_24px_rgba(0,0,0,0.08)] safe-area-inset-bottom"
          data-testid="payment-sticky-cta"
        >
          <div className="px-3 py-3">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">Total payable</p>
                <AnimatedPrice value={effectivePrice} className="text-2xl font-black text-slate-900" />
              </div>
              {totalSavings > 0 && (
                <div className="text-right">
                  <p className="text-[10px] text-green-700 font-semibold">You save</p>
                  <p className="text-sm font-bold text-green-600">₹{totalSavings.toLocaleString('en-IN')}</p>
                </div>
              )}
            </div>
            <Button
              type="button"
              onClick={(e) => {
                // Submit the form
                const form = document.querySelector('[data-testid="payment-form"]');
                if (form) form.requestSubmit();
                else handleCashfreePay(e);
              }}
              disabled={loading || !cfLoaded || planLoading}
              data-testid="payment-pay-btn-mobile"
              className="w-full bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 hover:from-blue-700 hover:via-indigo-700 hover:to-indigo-800 text-white shadow-xl shadow-blue-500/40 transition-all active:scale-[0.98] py-6 text-base font-bold rounded-xl relative overflow-hidden"
            >
              {/* Shimmer */}
              <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full animate-[shimmer_2.4s_infinite] pointer-events-none" />
              {loading ? (
                <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Starting payment…</>
              ) : !cfLoaded ? (
                <>Loading secure checkout…</>
              ) : (
                <span className="relative flex items-center justify-center gap-2">
                  <Lock className="w-4 h-4" />
                  Pay Securely
                  <Zap className="w-4 h-4" />
                </span>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Shimmer keyframe (Tailwind doesn't have it built-in) */}
      <style>{`
        @keyframes shimmer { 100% { transform: translateX(100%); } }
        .safe-area-inset-top { padding-top: env(safe-area-inset-top, 0); }
        .safe-area-inset-bottom { padding-bottom: env(safe-area-inset-bottom, 0); }
      `}</style>
    </div>
  );
};

export default Payment;
